/**
 * 전역 공용 타입
 * - 도메인 전용 타입은 각 `src/views/<domain>/types.ts` 에 둔다.
 */

export type DateTime = string | number | Date | null | undefined;

export interface SelectItem<T = string> {
  value: T;
  title: string;
}

/** 페이지/산출물 진행 상태 (설계 화면 공통) */
export type DesignStatus =
  "NOT_STARTED" | "IN_PROGRESS" | "REVIEW" | "CONFIRMED";

/** DesignStatus 의 별칭. 페이지 단위 진행 상태를 가리킬 때 사용한다. */
export type PageStatus = DesignStatus;

export const DESIGN_STATUS_LABELS: Record<DesignStatus, string> = {
  NOT_STARTED: "시작 전",
  IN_PROGRESS: "진행중",
  REVIEW: "검토중",
  CONFIRMED: "확정",
} as const;

export interface ConfirmResolveResponse {
  uuid: string;
  value: boolean;
}

export interface Confirm {
  uuid: string;
  title: string;
  content: string;
  cancelButtonTitle: string;
  confirmButtonTitle: string;
  /** 확인 버튼 모양. `danger` 는 되돌릴 수 없는 동작(삭제 · 제거 · 폐기)용 빨간 버튼. 없으면 기본(primary). */
  tone?: ConfirmTone;
  /** 확인창 본문 아래 「영향 자세히」 접힘(선택, 90차 C90-26) */
  impactDetails?: string[];
  resolver: (value: ConfirmResolveResponse) => void;
}

export type ConfirmTone = "default" | "danger";

export interface JwtTokens {
  accessToken: string;
  refreshToken: string;
}

/** 토큰 주체 구분. ADMIN 은 SaaS 운영자, USER 는 고객사 구성원. (백엔드 UserTypeEnum) */
export type UserType = "ADMIN" | "USER";

/** 워크스페이스 내 구성원 역할 (백엔드 WorkspaceRoleEnum) */
export type WorkspaceRole = "OWNER" | "HR_ADMIN" | "INTERVIEWER" | "VIEWER";

/** `WorkspaceRole` 의 별칭. 새 코드에서는 `WorkspaceRole` 을 쓴다. */
export type UserRole = WorkspaceRole;

export const WORKSPACE_ROLE_LABELS: Record<WorkspaceRole, string> = {
  OWNER: "소유자",
  HR_ADMIN: "인사 담당자",
  INTERVIEWER: "면접관",
  VIEWER: "조회 전용",
} as const;

/** SaaS 운영자(Admin) 전용 권한 목록 (백엔드 AuthorityEnum) */
export type Authority =
  | "ADMIN_VIEW"
  | "ADMIN_EDIT"
  | "WORKSPACE_VIEW"
  | "WORKSPACE_EDIT"
  | "TEMPLATE_VIEW"
  | "TEMPLATE_EDIT"
  | "PLAN_VIEW"
  | "PLAN_EDIT";

/** 액세스 토큰 클레임 (백엔드 AccessTokenClaims) */
export interface TokenClaims {
  id: number; // ID(KEY)
  loginId: string; // 로그인 아이디(이메일)
  name: string; // 이름
  type: UserType | ""; // 사용자 유형 (초기 상태: "")
  managerFlag: boolean; // 매니저 여부(모든 권한 소유)
  authorities: Authority[]; // 권한 목록 (Admin 전용)
  workspaceId: number | null; // 선택된 워크스페이스 ID (User 전용)
  role: WorkspaceRole | null; // 워크스페이스 내 역할 (User 전용)
}

/**
 * 워크스페이스 요약 (백엔드 WorkspaceSimpleDto)
 * - 로그인 응답의 `workspaces[]` 항목이자, 사이드바 카드에 표시하는 현재 워크스페이스다.
 * - `role` 의 `""` 는 로그인 전 초기 상태를 뜻한다.
 */
export interface Workspace {
  id: number;
  name: string;
  role: WorkspaceRole | "";
  plan: string;
}

export interface Id {
  id?: number;
}

export interface UserSimple extends Id {
  loginId: string; // 로그인 아이디
  name: string; // 이름
}

export interface IdCreatedUpdated extends Id {
  createdBy?: UserSimple;
  createdAt?: DateTime;
  updatedBy?: UserSimple;
  updatedAt?: DateTime;
}

export interface ListApiResult<T> {
  page: number;
  pageSize: number;
  total: number;
  items: T[];
}

/**
 * 로그인 요청 (백엔드 AuthLogin)
 * - 비밀번호는 **평문으로 전송**한다. 서버가 bcrypt 로 검증하므로 클라이언트 선해시를 하지 않는다.
 * - `workspaceId` 미지정 시 소속된 첫 워크스페이스가 선택된다.
 */
export interface LoginRequest {
  loginId: string;
  password: string;
  workspaceId?: number;
}

/** 로그인 응답 (백엔드 AuthLoginResponse) */
export interface LoginResponse extends JwtTokens {
  workspaceId: number;
  role: WorkspaceRole;
  workspaces: Workspace[];
}

/** 내 정보 조회 응답 (백엔드 AuthMeResponse, `GET api/v1/auth/me`) */
export interface AuthMeResponse {
  id: number;
  loginId: string;
  name: string;
  workspaceId: number;
  role: WorkspaceRole;
  workspaces: Workspace[];
}

/* ───────── 담당(duty) · 공통 검토(design review) — 개편 반영(renewal-1013) 계약 C1 · C2 ───────── */

/**
 * 구성원 담당(백엔드 WorkspaceDutyEnum). 권한(`WorkspaceRole`)과 별개의 태그다 — JWT 클레임에 없고 구성원 목록 `duties[]` 로 온다.
 * 면접관 담당은 권한이 VIEWER 면 줄 수 없고(400 `WORKSPACE_DUTY_ROLE_TOO_LOW`), 검토자 담당은 권한과 무관하다.
 */
export type WorkspaceDuty =
  | "INTERVIEWER_1"
  | "INTERVIEWER_2"
  | "INTERVIEWER_3"
  | "JOB_REVIEWER"
  | "COMPETENCY_REVIEWER";

export const WORKSPACE_DUTY_LABELS: Record<WorkspaceDuty, string> = {
  INTERVIEWER_1: "1차 면접관",
  INTERVIEWER_2: "2차 면접관",
  INTERVIEWER_3: "3차 면접관",
  JOB_REVIEWER: "직무 검토자",
  COMPETENCY_REVIEWER: "역량 검토자",
} as const;

/**
 * 담당 영역(백엔드 WorkspaceAreaEnum). 허용 조합은 서버가 판정한다(그 밖 400 `WORKSPACE_DUTY_AREA_INVALID`):
 * 면접관 → OPERATIONS · ALL, 직무 검토자 → JOB_ANALYSIS · ALL, 역량 검토자 → COMPETENCY · ALL.
 */
export type WorkspaceArea =
  "ALL" | "ORG_FIT" | "JOB_ANALYSIS" | "COMPETENCY" | "OPERATIONS";

export const WORKSPACE_AREA_LABELS: Record<WorkspaceArea, string> = {
  ALL: "전체 설계",
  ORG_FIT: "조직적합성 설계",
  JOB_ANALYSIS: "직무분석",
  COMPETENCY: "역량모델",
  OPERATIONS: "채용 운영",
} as const;

/** 구성원 담당 한 줄(구성원 목록 · 초대 응답의 `duties[]`). `jobId` 가 없으면 모든 직무 */
export interface WorkspaceMemberDuty {
  id: number;
  duty: WorkspaceDuty;
  area: WorkspaceArea;
  jobId: number | null;
  jobTitle: string | null;
}

/** 검토 대상(백엔드 DesignReviewTargetEnum). targetId 는 TALENT = 인재상 프로필 id, 나머지 = jobId */
export type DesignReviewTarget = "TALENT" | "JOB_ANALYSIS" | "JOB_COMPETENCY";

export const DESIGN_REVIEW_TARGET_LABELS: Record<DesignReviewTarget, string> = {
  TALENT: "인재상 합의",
  JOB_ANALYSIS: "직무분석 검토",
  JOB_COMPETENCY: "직무역량 검토",
} as const;

/**
 * 검토 요청 상태(백엔드 DesignReviewStatusEnum). 서버가 응답마다 다시 계산한다 — 대기자가 있으면 REQUESTED,
 * 전원 응답 + 전원 동의면 AGREED, 아니면 CHANGES_REQUESTED. 「내용 바뀜」 은 상태가 아니라 `stale` 이다.
 */
export type DesignReviewStatus =
  "REQUESTED" | "AGREED" | "CHANGES_REQUESTED" | "CANCELLED";

export const DESIGN_REVIEW_STATUS_LABELS: Record<DesignReviewStatus, string> = {
  REQUESTED: "검토 중",
  AGREED: "동의 완료",
  CHANGES_REQUESTED: "수정 요청",
  CANCELLED: "취소",
} as const;

/** 검토자 그룹(백엔드 ReviewerGroupEnum). 인재상 합의는 EXECUTIVE · FIELD 두 그룹, 직무 · 역량 검토는 SME 한 그룹 */
export type ReviewerGroup = "EXECUTIVE" | "FIELD" | "SME";

export const REVIEWER_GROUP_LABELS: Record<ReviewerGroup, string> = {
  EXECUTIVE: "경영진 · 인사 그룹",
  FIELD: "현장 관리자 그룹",
  SME: "검토자",
} as const;

/** 검토자 응답(백엔드 ReviewVerdictEnum) */
export type ReviewVerdict = "PENDING" | "AGREE" | "REVISE";

/** 검토자의 항목별 의견(`itemKey` 는 대상 화면이 정한 항목 키, ≤100자) */
export interface DesignReviewItemComment {
  itemKey: string;
  comment: string;
}

/** 지정 검토자 한 명 */
export interface DesignReviewer {
  userId: number;
  name: string;
  /** 옛 행 · 그룹 없이 지정된 검토자는 null — 화면은 「검토자」(SME) 묶음으로 본다 */
  group: ReviewerGroup | null;
  verdict: ReviewVerdict;
  comment: string | null;
  itemComments: DesignReviewItemComment[];
  respondedAt: string | null;
  /** 이 검토자가 동의한 뒤 대상 내용이 바뀌었다 */
  stale: boolean;
  /**
   * 지금 응답할 수 있는 검토자인가(92차 S92-2 · 서버 응답 가드와 같은 판정 — 사용 중 구성원 + 담당 유지). 제거 · 사용 중지 · 담당 해제면 false.
   * 열린 요청(REQUESTED)에서만 계산하고 닫힌 요청은 늘 true. 필드가 없는 옛 서버는 true 로 본다(`!== false`).
   */
  active?: boolean;
}

/**
 * 공통 검토 응답(백엔드 DesignReviewResponse — 인재상 합의 · 직무분석 검토 · 직무역량 검토가 같은 모양).
 * 「통과」 = `status === "AGREED"` 이고 `stale === false`(서버 `review_passed()` 와 같은 판정).
 */
export interface DesignReview {
  id: number;
  targetType: DesignReviewTarget;
  targetId: number;
  status: DesignReviewStatus;
  /** 요청 뒤 대상 내용이 바뀌었다(조회 시 계산 — 상태 컬럼은 그대로) */
  stale: boolean;
  message: string | null;
  requestedBy: { userId: number; name: string };
  requestedAt: string;
  completedAt: string | null;
  reviewers: DesignReviewer[];
  /**
   * 열린 요청이 멈췄다(92차 S92-2 · LK92-02) — 아직 응답하지 않은 검토자 중 `active === false` 가 있어 모두 동의가 불가능하다. 요청자가 취소 후 다시 요청할 일.
   * 필드가 없는 옛 서버는 멈추지 않은 것으로 본다(`=== true` 로만 판정).
   */
  stalled?: boolean;
  /** 지금 대상 내용 요약(sha256 64자). 응답할 때 `expectedBasisKey` 로 그대로 되돌려 보낸다(RV-CORE-01) */
  currentBasisKey: string;
}

/** 내 검토함 한 줄(`GET api/v1/design-reviews/mine`) */
export interface DesignReviewInboxItem {
  reviewId: number;
  targetType: DesignReviewTarget;
  targetId: number;
  /** 대상 이름(인재상 · 직무명) */
  targetLabel: string;
  jobId: number | null;
  requestedByName: string;
  requestedAt: string;
  /** 그룹 없이 지정됐으면 null(「검토자」) */
  myGroup: ReviewerGroup | null;
  myVerdict: ReviewVerdict;
  status: DesignReviewStatus;
  stale: boolean;
  /**
   * 지금 이 요청에 응답할 수 있는가(93차 S93-1 · LK93-01) — 담당이 해제 · 사용 중지 · 제거된 검토자는 목록에 남지만 `false`(응답 가드와 같은 판정).
   * 필드가 없는 옛 서버는 응답 가능으로 본다(`=== false` 로만 판정).
   */
  canRespond?: boolean;
  /** 이동할 화면 pageId(`navigation.ts`) */
  navMenuKey: string;
  navJobId: number | null;
}

/**
 * 내 검토함 응답(`GET api/v1/design-reviews/mine`) — 목록 모양 + `respondableTotal`(93차 S93-5): 열린 요청(REQUESTED)이고 내가 응답할 수 있는(`canRespond`) 내 행 **전체** 수(쪽과 무관).
 * 필드가 없는 옛 서버는 화면이 읽은 항목으로 센다.
 */
export interface DesignReviewInboxResult extends ListApiResult<DesignReviewInboxItem> {
  respondableTotal?: number;
}

/** 검토 응답 요청(`POST api/v1/design-reviews/{reviewId}/respond`) */
export interface DesignReviewRespondRequest {
  verdict: Exclude<ReviewVerdict, "PENDING">;
  /**
   * 응답한 화면이 본 내용 요약 = 받은 `DesignReview.currentBasisKey`(필수). 그새 서버 내용이 바뀌었으면
   * 400 `DESIGN_REVIEW_BASIS_CHANGED` data `{currentBasisKey}` — 다시 불러온 뒤 응답한다
   */
  expectedBasisKey: string;
  /** ≤2000자 */
  comment?: string;
  /** ≤30개, 항목 키 ≤100자 · 의견 ≤1000자 */
  itemComments?: DesignReviewItemComment[];
}

/** 검토자 후보(검토 패널의 체크 칩 한 칸). `hint` 는 동명이인 구분용 보조 글(담당 · 직무) */
export interface DesignReviewCandidate {
  userId: number;
  name: string;
  hint?: string;
}

/** 인재상 합의 후보 — 두 그룹을 따로 고른다(같은 사람을 두 그룹에 둘 수 없다) */
export interface ConsensusReviewCandidates {
  executive: DesignReviewCandidate[];
  field: DesignReviewCandidate[];
}

/** 검토 요청 본문의 검토자 부분 — 직무 · 역량 검토는 `reviewerIds`, 인재상 합의는 두 그룹 */
export type DesignReviewRequestPayload =
  | { reviewerIds: number[] }
  | { executiveReviewerIds: number[]; fieldReviewerIds: number[] };
